/**
 * Schema.org VideoObject JSON-LD for watch pages. Of all schema types
 * this LMS emits, VideoObject is the highest-leverage rich result —
 * Google still produces video carousels + the video result tab from
 * it; AI search reads it as the primary signal about what kind of
 * content the page hosts.
 *
 * <p>Host-agnostic: caller passes plain fields ({@link VideoSchemaInput})
 * rather than a host-specific entity type.
 */
import { JsonLdScript } from './_shared';

export interface VideoSchemaInput {
  title?: string;
  description?: string;
  subtitle?: string;
  posterUrl?: string;
  /** ISO 8601 date — when the video aired/published. */
  airDate?: string;
  durationSeconds?: number;
  /** Direct video URL (e.g. MP4) — present for shorts. */
  videoUrl?: string;
  /** Player media id — used to build {@code embedUrl} when {@code videoUrl}
   *  isn't set. */
  mediaId?: string;
  language?: string;
  tags?: string[];
  director?: string;
  channelName?: string;
  channelAvatar?: string;
}

export interface VideoObjectSchemaJsonLdProps {
  video: VideoSchemaInput;
  tenantSlug: string;
  canonicalUrl: string;
  providerName?: string;
  providerLogoUrl?: string;
  /** Override the embed-URL base. Defaults to
   *  {@code https://cinema8.com/embed} — the Cinema8 player this LMS
   *  uses for long-form videos. Pass a tenant-specific base when using
   *  a different host. */
  embedUrlBase?: string;
  id?: string;
}

const DEFAULT_EMBED_BASE = 'https://cinema8.com/embed';

export function VideoObjectSchemaJsonLd({
  video,
  tenantSlug,
  canonicalUrl,
  providerName,
  providerLogoUrl,
  embedUrlBase,
  id,
}: VideoObjectSchemaJsonLdProps) {
  const schema = buildVideoObjectSchema({
    video,
    tenantSlug,
    canonicalUrl,
    providerName: providerName ?? tenantSlug,
    providerLogoUrl,
    embedUrlBase,
  });
  return <JsonLdScript schema={schema} id={id ?? 'schema-videoobject'} />;
}

export function buildVideoObjectSchema(args: {
  video: VideoSchemaInput;
  tenantSlug: string;
  canonicalUrl: string;
  providerName: string;
  providerLogoUrl?: string;
  embedUrlBase?: string;
}) {
  const { video, canonicalUrl, providerName, providerLogoUrl, embedUrlBase } = args;
  const base = embedUrlBase ?? DEFAULT_EMBED_BASE;

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title || 'Untitled video',
    description: video.description || video.subtitle || video.title || 'Video',
    url: canonicalUrl,
    thumbnailUrl: video.posterUrl,
    uploadDate: video.airDate,
    duration: secondsToIso8601(video.durationSeconds),
    contentUrl: video.videoUrl,
    embedUrl: video.mediaId ? `${base}/${encodeURIComponent(video.mediaId)}` : undefined,
    inLanguage: video.language,
    genre: video.tags && video.tags.length > 0 ? video.tags.join(', ') : undefined,
    director: video.director
      ? {
          '@type': 'Person',
          name: video.director,
        }
      : undefined,
    author: video.channelName
      ? {
          '@type': 'Organization',
          name: video.channelName,
          image: video.channelAvatar,
        }
      : undefined,
    publisher: {
      '@type': 'Organization',
      name: providerName,
      logo: providerLogoUrl
        ? {
            '@type': 'ImageObject',
            url: providerLogoUrl,
          }
        : undefined,
    },
  };
}

function secondsToIso8601(seconds: number | undefined): string | undefined {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return undefined;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  let out = 'PT';
  if (h > 0) out += `${h}H`;
  if (m > 0) out += `${m}M`;
  if (s > 0 || (h === 0 && m === 0)) out += `${s}S`;
  return out;
}
